import { FC } from 'react';
import { Button, Drawer } from 'antd';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { H5 } from 'components/ui/base/typography';
import FormInput from 'components/new-ui/composed/formInput/FormInput';
import FormSelect from 'components/new-ui/composed/formSelect/FormSelect';
import { getRequiredMessage } from 'views/declarations/utils/validation-utils';
import { getFormikProps } from 'views/declarations/utils/form-utils';
import { StyledCloseOutlined } from 'views/declarations/declaration-tabs/documentation/CustomerDocuments.styles';
import LabeledField from '../components/LabeledField';
import TeamSelectField from '../components/TeamSelectField';
import { itemFieldTemplatePath } from './ItemFieldTemplatesTable';
import {
    ITEM_FIELD_TEMPLATES_COUNTRY,
    ITEM_FIELD_TEMPLATES_FORM_TYPE,
    ITEM_FIELD_TEMPLATES_INTERNAL_TYPE,
} from '../constants';

export type ItemFieldTemplateLocationState = {
    name: string;
    teamId?: string;
};

type FormValues = {
    country: string;
    declarationType: string;
    formType: string;
    name: string;
    teamId?: string;
};

interface Props {
    open?: boolean;
    onClose: () => void;
    isFreightForwarder?: boolean;
}

const validationSchema = Yup.object().shape({
    // FormInput turns an empty value into null
    name: Yup.string().trim().nullable().required(getRequiredMessage('field')),
});

// Phase 1: item field templates exist for UK Export B1 only, so these are shown but cannot be changed
const COUNTRY = ITEM_FIELD_TEMPLATES_COUNTRY.toUpperCase();
const INTERNAL_TYPE = ITEM_FIELD_TEMPLATES_INTERNAL_TYPE;
const FORM_TYPE = ITEM_FIELD_TEMPLATES_FORM_TYPE;

const initialValues: FormValues = {
    country: COUNTRY,
    declarationType: INTERNAL_TYPE,
    formType: FORM_TYPE,
    name: '',
};

const toOptions = (id: string) => [{ id, value: '' }];

const CreateItemFieldTemplateDrawer: FC<Props> = ({ open, onClose, isFreightForwarder = false }) => {
    const navigate = useNavigate();

    const handleSubmit = (values: FormValues) => {
        const state: ItemFieldTemplateLocationState = { name: values.name.trim(), teamId: values.teamId };

        formik.resetForm();
        onClose();
        navigate(itemFieldTemplatePath(), { state });
    };

    const formik = useFormik<FormValues>({
        initialValues,
        validationSchema,
        validateOnChange: false,
        onSubmit: handleSubmit,
    });

    const handleCancel = () => {
        formik.resetForm();
        onClose();
    };

    return (
        <Drawer open={open} width={550} onClose={handleCancel} placement="right" headerStyle={{ display: 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ display: 'inline-flex', gap: '16px' }}>
                    <StyledCloseOutlined onClick={handleCancel} />
                    <H5>New Template</H5>
                </div>
                <div style={{ display: 'flex', gap: '1rem' }}>
                    <Button onClick={handleCancel} style={{ borderRadius: '6px' }}>
                        Cancel
                    </Button>
                    <Button type="primary" onClick={formik.submitForm} style={{ borderRadius: '6px' }}>
                        Create
                    </Button>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '2rem' }}>
                <LabeledField label="Country" htmlFor="country" required>
                    <FormSelect
                        style={{ maxWidth: '100%' }}
                        disabled
                        hideKeys
                        selectOptions={toOptions(COUNTRY)}
                        {...getFormikProps('country', formik)}
                    />
                </LabeledField>
                <LabeledField label="Declaration Type" htmlFor="declarationType" required>
                    <FormSelect
                        style={{ maxWidth: '100%' }}
                        disabled
                        hideKeys
                        selectOptions={toOptions(INTERNAL_TYPE)}
                        {...getFormikProps('declarationType', formik)}
                    />
                </LabeledField>
                <LabeledField label="Form Type" htmlFor="formType" required>
                    <FormSelect
                        style={{ maxWidth: '100%' }}
                        disabled
                        hideKeys
                        selectOptions={toOptions(FORM_TYPE)}
                        {...getFormikProps('formType', formik)}
                    />
                </LabeledField>
                <LabeledField label="Template Name" htmlFor="name" required>
                    <FormInput
                        condensed
                        placeholder="Enter Template Name"
                        {...getFormikProps('name', formik)}
                        inputStyles={{
                            width: '100%',
                            maxWidth: '100%',
                        }}
                    />
                </LabeledField>
                {isFreightForwarder && (
                    <LabeledField label="Team" htmlFor="teamId">
                        <TeamSelectField
                            fieldHelper={formik.getFieldHelpers('teamId')}
                            fieldMeta={formik.getFieldMeta('teamId')}
                            fieldProps={formik.getFieldProps('teamId')}
                        />
                    </LabeledField>
                )}
            </div>
        </Drawer>
    );
};

export default CreateItemFieldTemplateDrawer;
